import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Paperclip, 
  Download, 
  Trash2, 
  Search,
  Cloud,
  FileText,
  Image,
  Video,
  Music,
  Filter,
  SortAsc,
  SortDesc,
  ExternalLink
} from 'lucide-react';
import attachmentService from '../services/attachmentService';

const AttachmentsPage = () => {
  const navigate = useNavigate();
  const [attachments, setAttachments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  // const [loading, setLoading] = useState(true);
  // const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterBy, setFilterBy] = useState('all');

  const loadAttachments = useCallback(async () => {
    try {
      const result = await attachmentService.getAllAttachments();
      if (result.success) {
        setAttachments(result.data);
      }
    } catch (error) {
      console.error('Error loading attachments:', error);
    }
  }, []);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const filteredAttachments = (attachments || [])
    .filter(attachment => {
      const matchesSearch = searchTerm === '' || 
                           attachment.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           attachment.question_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           attachment.uploader_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           attachment.module_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           attachment.question_category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           attachment.question_priority?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           attachment.question_status?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesFilter = filterBy === 'all' || attachment.file_type === filterBy;
      
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (sortBy === 'created_at') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      if (sortOrder === 'desc') {
        return bValue > aValue ? 1 : -1;
      } else {
        return aValue > bValue ? 1 : -1;
      }
    });

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image': return <Image className="h-5 w-5 text-green-600" />;
      case 'pdf': return <FileText className="h-5 w-5 text-red-600" />;
      case 'video': return <Video className="h-5 w-5 text-blue-600" />;
      case 'audio': return <Music className="h-5 w-5 text-purple-600" />;
      default: return <Paperclip className="h-5 w-5 text-gray-600" />;
    }
  };

  const getFileTypeColor = (fileType) => {
    switch (fileType) {
      case 'image': return 'bg-green-100 text-green-700';
      case 'pdf': return 'bg-red-100 text-red-700';
      case 'video': return 'bg-blue-100 text-blue-700';
      case 'audio': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatFileSize = (bytes) => {
    return attachmentService.formatFileSize(bytes);
  };

  const handleDownload = async (attachment) => {
    try {
      await attachmentService.downloadAttachment(attachment);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleDelete = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;
    
    try {
      const result = await attachmentService.deleteAttachment(attachmentId);
      if (result.success) {
        setAttachments(prev => prev.filter(att => att.id !== attachmentId));
      }
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handlePushToDataroom = async (attachment) => {
    // This would integrate with dataroom functionality
    // For now, we'll show a mock implementation
    const projects = [
      { id: 1, name: 'Project Alpha' },
      { id: 2, name: 'Project Beta' },
      { id: 3, name: 'Project Gamma' }
    ];
    
    const selectedProject = window.prompt(`Push "${attachment.filename}" to which dataroom?\n\nAvailable projects:\n${projects.map(p => `${p.id}. ${p.name}`).join('\n')}`);
    
    if (selectedProject) {
      const projectId = parseInt(selectedProject);
      const project = projects.find(p => p.id === projectId);
      
      if (project) {
        window.alert(`File "${attachment.filename}" has been pushed to ${project.name} dataroom!`);
        // Here you would implement the actual push functionality
      }
    }
  };

  const getUniqueFileTypes = () => {
    const types = [...new Set(attachments.map(att => att.file_type))];
    return types;
  };

  const handleQuestionClick = (questionId) => {
    navigate(`/questions/${questionId}`);
  };

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">
        Question Attachments
      </h1>
      <p className="text-gray-600 mb-8">
        Track and manage all attachments shared through questions across your organization
      </p>


      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Search by filename, question, module, category, priority, or status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* File Type Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <select
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
            >
              <option value="all">All File Types</option>
              {getUniqueFileTypes().map(type => (
                <option key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Sort */}
          <div className="flex gap-2">
            <select
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="created_at">Sort by Date</option>
              <option value="filename">Sort by Name</option>
              <option value="file_size">Sort by Size</option>
              <option value="uploader_name">Sort by Uploader</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {sortOrder === 'desc' ? <SortDesc className="h-4 w-4" /> : <SortAsc className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Attachments Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  File
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Question
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Uploaded
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Uploader
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredAttachments.map((attachment) => (
                <tr key={attachment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getFileIcon(attachment.file_type)}
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          {attachment.filename}
                        </div>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getFileTypeColor(attachment.file_type)}`}>
                          {attachment.file_type}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      <button
                        onClick={() => handleQuestionClick(attachment.question_id)}
                        className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                      >
                        {attachment.question_title}
                        <ExternalLink className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="text-sm text-gray-500">
                      ID: {attachment.question_id}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatFileSize(attachment.file_size)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(attachment.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {attachment.uploader_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button 
                        onClick={() => handleDownload(attachment)}
                        className="text-blue-600 hover:text-blue-900" 
                        title="Download"
                      >
                        <Download className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handlePushToDataroom(attachment)}
                        className="text-purple-600 hover:text-purple-900" 
                        title="Push to Dataroom"
                      >
                        <Cloud className="h-4 w-4" />
                      </button>
                      <button 
                        onClick={() => handleDelete(attachment.id)}
                        className="text-red-600 hover:text-red-900" 
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredAttachments.length === 0 && (
          <div className="text-center py-12">
            <Paperclip className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No attachments found</h3>
            <p className="text-gray-500">Try adjusting your search or check back later</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttachmentsPage;